import { ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { NoCompanyState } from "@/components/company/NoCompanyState";
import { Loader2, Plus, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuickAddExpenseDialog } from "@/components/expenses/QuickAddExpenseDialog";
import { EarningsOnboardingOverlay } from "@/components/onboarding/EarningsOnboardingOverlay";
import { AnchorIcon } from "@/components/icons/AnchorIcon";
import { Sheet, SheetContent } from "@/components/ui/sheet";

interface AppLayoutProps {
  children: ReactNode;
}

// Pages where the floating "+ Add Expense" shortcut appears
const SHORTCUT_PREFIXES = ["/app/dashboard", "/app/decisions", "/app/operations"];

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { isLoading: authLoading, isAuthenticated } = useAuth({ requireAuth: true });
  const { company, isCompanyLoading } = useCompany();
  const location = useLocation();
  const [quickExpenseOpen, setQuickExpenseOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const showShortcut = SHORTCUT_PREFIXES.some((p) => location.pathname.startsWith(p));

  if (authLoading || isCompanyLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!company) {
    return <NoCompanyState />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <AppSidebar />
      </div>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-card border-b border-border flex items-center px-4 gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setMobileNavOpen(true)}>
          <Menu className="h-5 w-5" />
        </Button>
        <AnchorIcon variant="blue" className="h-5 w-5" />
        <span className="font-bold text-foreground text-sm">HostCaptain</span>
      </div>

      {/* Mobile sidebar drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <AppSidebar onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="md:pl-64 pt-14 md:pt-0 min-h-screen">
        {children}
      </main>

      {/* First-time onboarding: force earnings import */}
      <EarningsOnboardingOverlay />

      {/* Global quick-add expense shortcut */}
      {showShortcut && (
        <Button
          onClick={() => setQuickExpenseOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-12 rounded-full shadow-lg gap-2 px-5"
          size="lg"
        >
          <Plus className="h-5 w-5" />
          Add Expense
        </Button>
      )}

      <QuickAddExpenseDialog
        open={quickExpenseOpen}
        onOpenChange={setQuickExpenseOpen}
      />
    </div>
  );
};
